/**
 * LAWITY - Expanded Client Form Component
 * Multi-tab interface for comprehensive client data entry
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Building, Mail, Phone, MapPin, CreditCard, FileCheck,
  Settings, Users, X, Save, Loader2, ChevronRight, Plus, Trash2,
  Calendar, Globe, Shield, Bell, Hash, Percent, DollarSign,
  AlertCircle, CheckCircle, Info
} from 'lucide-react';
import type {
  ClientFormData,
  ClientType,
  ClientStatus,
  EntityType,
  BillingMethod,
  PaymentTerms,
  ContactMethod,
  Address,
  RelatedParty,
  RelationshipType,
} from '../../types/practice';

// Tab definitions
const TABS = [
  { id: 'basic', label: 'Basic Info', icon: User },
  { id: 'contact', label: 'Contact', icon: Mail },
  { id: 'addresses', label: 'Addresses', icon: MapPin },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'preferences', label: 'Preferences', icon: Settings },
] as const;

type TabId = typeof TABS[number]['id'];

// Entity type options
const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'trust', label: 'Trust' },
  { value: 'estate', label: 'Estate' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'sole_prop', label: 'Sole Proprietorship' },
];

// Status options
const STATUS_OPTIONS: { value: ClientStatus; label: string; color: string }[] = [
  { value: 'prospect', label: 'Prospect', color: 'text-blue-400 bg-blue-500/20' },
  { value: 'active', label: 'Active', color: 'text-green-400 bg-green-500/20' },
  { value: 'inactive', label: 'Inactive', color: 'text-gray-400 bg-gray-500/20' },
  { value: 'former', label: 'Former', color: 'text-yellow-400 bg-yellow-500/20' },
  { value: 'declined', label: 'Declined', color: 'text-red-400 bg-red-500/20' },
  { value: 'conflict', label: 'Conflict', color: 'text-orange-400 bg-orange-500/20' },
];

// Billing methods
const BILLING_METHODS: { value: BillingMethod; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'flat_fee', label: 'Flat Fee' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'hybrid', label: 'Hybrid' },
];

// Payment terms
const PAYMENT_TERMS: { value: PaymentTerms; label: string }[] = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
];

// Contact methods
const CONTACT_METHODS: { value: ContactMethod; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'mail', label: 'Mail' },
  { value: 'portal', label: 'Client Portal' },
];

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// Default empty address
const emptyAddress: Address = {
  street1: '',
  street2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'USA',
};

// Default form data
const defaultFormData: ClientFormData = {
  type: 'individual',
  status: 'prospect',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preferredContactMethod: 'email',
  primaryAddress: { ...emptyAddress },
  mailingAddressSameAsPrimary: true,
  billingAddressSameAsPrimary: true,
  billingMethod: 'hourly',
  paymentTerms: 'net_30',
  taxExempt: false,
  engagementLetterSigned: false,
  conflictCheckCompleted: false,
  identityVerified: false,
  portalAccessEnabled: false,
  receiveInvoiceEmails: true,
  receiveMatterUpdates: true,
  receiveMarketingEmails: false,
};

interface ClientFormProps {
  initialData?: Partial<ClientFormData>;
  onSave: (data: ClientFormData) => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
}

export function ClientForm({ initialData, onSave, onCancel, isEditing = false }: ClientFormProps) {
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [formData, setFormData] = useState<ClientFormData>({ ...defaultFormData, ...initialData });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Update form field
  const updateField = <K extends keyof ClientFormData>(field: K, value: ClientFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Update address field
  const updateAddress = (
    addressType: 'primaryAddress' | 'mailingAddress' | 'billingAddress',
    field: keyof Address,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...(prev[addressType] || emptyAddress),
        [field]: value,
      },
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic validation
    if (formData.type === 'individual') {
      if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    } else {
      if (!formData.entityName?.trim()) newErrors.entityName = 'Entity name is required';
      if (!formData.entityType) newErrors.entityType = 'Entity type is required';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone is required';
    }

    // Address validation
    if (!formData.primaryAddress.street1?.trim()) {
      newErrors['primaryAddress.street1'] = 'Street address is required';
    }
    if (!formData.primaryAddress.city?.trim()) {
      newErrors['primaryAddress.city'] = 'City is required';
    }
    if (!formData.primaryAddress.state?.trim()) {
      newErrors['primaryAddress.state'] = 'State is required';
    }
    if (!formData.primaryAddress.postalCode?.trim()) {
      newErrors['primaryAddress.postalCode'] = 'Postal code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      // Switch to tab with first error
      const errorFields = Object.keys(errors);
      if (errorFields.some(f => ['firstName', 'lastName', 'entityName', 'entityType'].includes(f))) {
        setActiveTab('basic');
      } else if (errorFields.some(f => ['email', 'phone'].includes(f))) {
        setActiveTab('contact');
      } else if (errorFields.some(f => f.startsWith('primaryAddress'))) {
        setActiveTab('addresses');
      }
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving client:', error);
    } finally {
      setSaving(false);
    }
  };

  // Check if tab has errors
  const tabHasErrors = (tabId: TabId): boolean => {
    const errorFields = Object.keys(errors);
    switch (tabId) {
      case 'basic':
        return errorFields.some(f => ['firstName', 'lastName', 'entityName', 'entityType', 'status'].includes(f));
      case 'contact':
        return errorFields.some(f => ['email', 'phone', 'preferredContactMethod'].includes(f));
      case 'addresses':
        return errorFields.some(f => f.includes('Address'));
      case 'billing':
        return errorFields.some(f => ['billingMethod', 'paymentTerms', 'defaultBillingRate'].includes(f));
      default:
        return false;
    }
  };

  return (
    <div className="h-full flex flex-col bg-primary-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
        <div>
          <h2 className="text-xl font-semibold text-white">
            {isEditing ? 'Edit Client' : 'New Client'}
          </h2>
          <p className="text-sm text-primary-400 mt-1">
            {formData.type === 'individual' ? 'Individual Client' : 'Business Entity'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-primary-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Save Changes' : 'Create Client'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-primary-700 px-6">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const hasError = tabHasErrors(tab.id);
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                isActive
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-primary-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {hasError && (
                <AlertCircle className="w-4 h-4 text-red-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'basic' && (
              <BasicInfoTab
                formData={formData}
                updateField={updateField}
                errors={errors}
              />
            )}
            {activeTab === 'contact' && (
              <ContactTab
                formData={formData}
                updateField={updateField}
                errors={errors}
              />
            )}
            {activeTab === 'addresses' && (
              <AddressesTab
                formData={formData}
                updateField={updateField}
                updateAddress={updateAddress}
                errors={errors}
              />
            )}
            {activeTab === 'billing' && (
              <BillingTab
                formData={formData}
                updateField={updateField}
                errors={errors}
              />
            )}
            {activeTab === 'compliance' && (
              <ComplianceTab
                formData={formData}
                updateField={updateField}
                errors={errors}
              />
            )}
            {activeTab === 'preferences' && (
              <PreferencesTab
                formData={formData}
                updateField={updateField}
                errors={errors}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-primary-700 bg-primary-800/30">
        <button
          onClick={() => {
            const currentIdx = TABS.findIndex(t => t.id === activeTab);
            if (currentIdx > 0) setActiveTab(TABS[currentIdx - 1].id);
          }}
          disabled={activeTab === TABS[0].id}
          className="flex items-center gap-2 px-4 py-2 text-primary-400 hover:text-white disabled:opacity-30 transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Previous
        </button>
        <div className="flex gap-1">
          {TABS.map((tab, idx) => (
            <div
              key={tab.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                tab.id === activeTab ? 'bg-accent-500' : 'bg-primary-600'
              }`}
            />
          ))}
        </div>
        <button
          onClick={() => {
            const currentIdx = TABS.findIndex(t => t.id === activeTab);
            if (currentIdx < TABS.length - 1) setActiveTab(TABS[currentIdx + 1].id);
          }}
          disabled={activeTab === TABS[TABS.length - 1].id}
          className="flex items-center gap-2 px-4 py-2 text-primary-400 hover:text-white disabled:opacity-30 transition-colors"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// TAB COMPONENTS
// =============================================================================

interface TabProps {
  formData: ClientFormData;
  updateField: <K extends keyof ClientFormData>(field: K, value: ClientFormData[K]) => void;
  errors: Record<string, string>;
}

// Basic Info Tab
function BasicInfoTab({ formData, updateField, errors }: TabProps) {
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Client Type Toggle */}
      <div>
        <label className="block text-sm font-medium text-primary-300 mb-2">Client Type</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => updateField('type', 'individual')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
              formData.type === 'individual'
                ? 'border-accent-500 bg-accent-500/10 text-accent-400'
                : 'border-primary-600 text-primary-400 hover:border-primary-500'
            }`}
          >
            <User className="w-5 h-5" />
            Individual
          </button>
          <button
            type="button"
            onClick={() => updateField('type', 'entity')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
              formData.type === 'entity'
                ? 'border-accent-500 bg-accent-500/10 text-accent-400'
                : 'border-primary-600 text-primary-400 hover:border-primary-500'
            }`}
          >
            <Building className="w-5 h-5" />
            Entity
          </button>
        </div>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-primary-300 mb-2">Status</label>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateField('status', opt.value)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                formData.status === opt.value
                  ? opt.color + ' border border-current'
                  : 'bg-primary-800 text-primary-400 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Individual Fields */}
      {formData.type === 'individual' && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-primary-300 mb-2">Prefix</label>
              <select
                value={formData.suffix || ''}
                onChange={(e) => updateField('suffix', e.target.value || undefined)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              >
                <option value="">None</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-primary-300 mb-2">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => updateField('firstName', e.target.value)}
                className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                  errors.firstName ? 'border-red-500' : 'border-primary-600'
                }`}
              />
              {errors.firstName && (
                <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-primary-300 mb-2">Middle Name</label>
              <input
                type="text"
                value={formData.middleName || ''}
                onChange={(e) => updateField('middleName', e.target.value || undefined)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-primary-300 mb-2">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName || ''}
                onChange={(e) => updateField('lastName', e.target.value)}
                className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                  errors.lastName ? 'border-red-500' : 'border-primary-600'
                }`}
              />
              {errors.lastName && (
                <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Suffix</label>
              <select
                value={formData.suffix || ''}
                onChange={(e) => updateField('suffix', e.target.value || undefined)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              >
                <option value="">None</option>
                <option value="Jr.">Jr.</option>
                <option value="Sr.">Sr.</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="Esq.">Esq.</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Date of Birth</label>
              <input
                type="date"
                value={formData.dateOfBirth || ''}
                onChange={(e) => updateField('dateOfBirth', e.target.value || undefined)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              SSN
              <span className="text-primary-500 text-xs ml-2">(Encrypted)</span>
            </label>
            <input
              type="password"
              value={formData.ssn || ''}
              onChange={(e) => updateField('ssn', e.target.value || undefined)}
              placeholder="XXX-XX-XXXX"
              className="w-full max-w-xs px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
            />
          </div>
        </div>
      )}

      {/* Entity Fields */}
      {formData.type === 'entity' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              Entity Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.entityName || ''}
              onChange={(e) => updateField('entityName', e.target.value)}
              className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                errors.entityName ? 'border-red-500' : 'border-primary-600'
              }`}
            />
            {errors.entityName && (
              <p className="text-red-400 text-xs mt-1">{errors.entityName}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">
                Entity Type <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.entityType || ''}
                onChange={(e) => updateField('entityType', e.target.value as EntityType)}
                className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                  errors.entityType ? 'border-red-500' : 'border-primary-600'
                }`}
              >
                <option value="">Select type...</option>
                {ENTITY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.entityType && (
                <p className="text-red-400 text-xs mt-1">{errors.entityType}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">State of Formation</label>
              <select
                value={formData.stateOfFormation || ''}
                onChange={(e) => updateField('stateOfFormation', e.target.value || undefined)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              >
                <option value="">Select state...</option>
                {US_STATES.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Formation Date</label>
              <input
                type="date"
                value={formData.formationDate || ''}
                onChange={(e) => updateField('formationDate', e.target.value || undefined)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">
                EIN
                <span className="text-primary-500 text-xs ml-2">(Encrypted)</span>
              </label>
              <input
                type="password"
                value={formData.ein || ''}
                onChange={(e) => updateField('ein', e.target.value || undefined)}
                placeholder="XX-XXXXXXX"
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
          </div>

          <div className="border-t border-primary-700 pt-4">
            <h4 className="text-sm font-medium text-primary-300 mb-3">Registered Agent</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-primary-400 mb-2">Agent Name</label>
                <input
                  type="text"
                  value={formData.registeredAgentName || ''}
                  onChange={(e) => updateField('registeredAgentName', e.target.value || undefined)}
                  className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes & Tags */}
      <div className="border-t border-primary-700 pt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-primary-300 mb-2">Referral Source</label>
          <input
            type="text"
            value={formData.referralSource || ''}
            onChange={(e) => updateField('referralSource', e.target.value || undefined)}
            placeholder="e.g., Website, Referral from..."
            className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary-300 mb-2">Notes</label>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => updateField('notes', e.target.value || undefined)}
            rows={3}
            className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none resize-none"
          />
        </div>
      </div>
    </div>
  );
}

// Contact Tab
function ContactTab({ formData, updateField, errors }: TabProps) {
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Primary Contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">Primary Contact</h3>

        <div>
          <label className="block text-sm font-medium text-primary-300 mb-2">
            Email <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              className={`w-full pl-10 pr-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                errors.email ? 'border-red-500' : 'border-primary-600'
              }`}
            />
          </div>
          {errors.email && (
            <p className="text-red-400 text-xs mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-primary-300 mb-2">Secondary Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
            <input
              type="email"
              value={formData.emailSecondary || ''}
              onChange={(e) => updateField('emailSecondary', e.target.value || undefined)}
              className="w-full pl-10 pr-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              Phone <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="(XXX) XXX-XXXX"
                className={`w-full pl-10 pr-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
                  errors.phone ? 'border-red-500' : 'border-primary-600'
                }`}
              />
            </div>
            {errors.phone && (
              <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">Secondary Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
              <input
                type="tel"
                value={formData.phoneSecondary || ''}
                onChange={(e) => updateField('phoneSecondary', e.target.value || undefined)}
                placeholder="(XXX) XXX-XXXX"
                className="w-full pl-10 pr-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">Fax</label>
            <input
              type="tel"
              value={formData.fax || ''}
              onChange={(e) => updateField('fax', e.target.value || undefined)}
              placeholder="(XXX) XXX-XXXX"
              className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">Website</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
              <input
                type="url"
                value={formData.website || ''}
                onChange={(e) => updateField('website', e.target.value || undefined)}
                placeholder="https://"
                className="w-full pl-10 pr-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Preferences */}
      <div className="border-t border-primary-700 pt-6 space-y-4">
        <h3 className="text-lg font-medium text-white">Contact Preferences</h3>

        <div>
          <label className="block text-sm font-medium text-primary-300 mb-2">Preferred Contact Method</label>
          <div className="flex flex-wrap gap-2">
            {CONTACT_METHODS.map((method) => (
              <button
                key={method.value}
                type="button"
                onClick={() => updateField('preferredContactMethod', method.value)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  formData.preferredContactMethod === method.value
                    ? 'bg-accent-500/20 text-accent-400 border border-accent-500'
                    : 'bg-primary-800 text-primary-400 border border-primary-600 hover:text-white'
                }`}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-primary-300 mb-2">Best Time to Contact</label>
          <input
            type="text"
            value={formData.bestTimeToContact || ''}
            onChange={(e) => updateField('bestTimeToContact', e.target.value || undefined)}
            placeholder="e.g., Weekdays 9am-5pm, Evenings after 6pm"
            className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
          />
        </div>
      </div>
    </div>
  );
}

// Addresses Tab
interface AddressesTabProps extends TabProps {
  updateAddress: (
    addressType: 'primaryAddress' | 'mailingAddress' | 'billingAddress',
    field: keyof Address,
    value: string
  ) => void;
}

function AddressesTab({ formData, updateField, updateAddress, errors }: AddressesTabProps) {
  return (
    <div className="space-y-8 max-w-2xl">
      {/* Primary Address */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Primary Address</h3>
        <AddressFields
          address={formData.primaryAddress}
          onChange={(field, value) => updateAddress('primaryAddress', field, value)}
          errors={errors}
          prefix="primaryAddress"
        />
      </div>

      {/* Mailing Address */}
      <div className="border-t border-primary-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Mailing Address</h3>
          <label className="flex items-center gap-2 text-sm text-primary-300 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.mailingAddressSameAsPrimary}
              onChange={(e) => updateField('mailingAddressSameAsPrimary', e.target.checked)}
              className="rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            Same as Primary
          </label>
        </div>
        {!formData.mailingAddressSameAsPrimary && (
          <AddressFields
            address={formData.mailingAddress || emptyAddress}
            onChange={(field, value) => updateAddress('mailingAddress', field, value)}
            errors={errors}
            prefix="mailingAddress"
          />
        )}
      </div>

      {/* Billing Address */}
      <div className="border-t border-primary-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Billing Address</h3>
          <label className="flex items-center gap-2 text-sm text-primary-300 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.billingAddressSameAsPrimary}
              onChange={(e) => updateField('billingAddressSameAsPrimary', e.target.checked)}
              className="rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            Same as Primary
          </label>
        </div>
        {!formData.billingAddressSameAsPrimary && (
          <AddressFields
            address={formData.billingAddress || emptyAddress}
            onChange={(field, value) => updateAddress('billingAddress', field, value)}
            errors={errors}
            prefix="billingAddress"
          />
        )}
      </div>
    </div>
  );
}

// Address Fields Component
function AddressFields({
  address,
  onChange,
  errors,
  prefix,
}: {
  address: Address;
  onChange: (field: keyof Address, value: string) => void;
  errors: Record<string, string>;
  prefix: string;
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-primary-300 mb-2">
          Street Address <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={address.street1}
          onChange={(e) => onChange('street1', e.target.value)}
          className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
            errors[`${prefix}.street1`] ? 'border-red-500' : 'border-primary-600'
          }`}
        />
        {errors[`${prefix}.street1`] && (
          <p className="text-red-400 text-xs mt-1">{errors[`${prefix}.street1`]}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-primary-300 mb-2">Street Address 2</label>
        <input
          type="text"
          value={address.street2 || ''}
          onChange={(e) => onChange('street2', e.target.value)}
          placeholder="Apt, Suite, Unit, etc."
          className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
        />
      </div>

      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-3">
          <label className="block text-sm font-medium text-primary-300 mb-2">
            City <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={address.city}
            onChange={(e) => onChange('city', e.target.value)}
            className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
              errors[`${prefix}.city`] ? 'border-red-500' : 'border-primary-600'
            }`}
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-medium text-primary-300 mb-2">
            State <span className="text-red-400">*</span>
          </label>
          <select
            value={address.state}
            onChange={(e) => onChange('state', e.target.value)}
            className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
              errors[`${prefix}.state`] ? 'border-red-500' : 'border-primary-600'
            }`}
          >
            <option value="">--</option>
            {US_STATES.map((st) => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-primary-300 mb-2">
            ZIP <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={address.postalCode}
            onChange={(e) => onChange('postalCode', e.target.value)}
            className={`w-full px-3 py-2.5 bg-primary-800 border text-white rounded-lg focus:border-accent-500 outline-none ${
              errors[`${prefix}.postalCode`] ? 'border-red-500' : 'border-primary-600'
            }`}
          />
        </div>
      </div>
    </div>
  );
}

// Billing Tab
function BillingTab({ formData, updateField, errors }: TabProps) {
  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Billing Defaults</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">Billing Method</label>
            <div className="flex flex-wrap gap-2">
              {BILLING_METHODS.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => updateField('billingMethod', method.value)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    formData.billingMethod === method.value
                      ? 'bg-accent-500/20 text-accent-400 border border-accent-500'
                      : 'bg-primary-800 text-primary-400 border border-primary-600 hover:text-white'
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Default Hourly Rate</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.defaultBillingRate || ''}
                  onChange={(e) => updateField('defaultBillingRate', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Payment Terms</label>
              <select
                value={formData.paymentTerms}
                onChange={(e) => updateField('paymentTerms', e.target.value as PaymentTerms)}
                className="w-full px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              >
                {PAYMENT_TERMS.map((term) => (
                  <option key={term.value} value={term.value}>{term.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Retainer Required</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.retainerRequired || ''}
                  onChange={(e) => updateField('retainerRequired', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Credit Limit</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.creditLimit || ''}
                  onChange={(e) => updateField('creditLimit', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Information */}
      <div className="border-t border-primary-700 pt-6">
        <h3 className="text-lg font-medium text-white mb-4">Tax Information</h3>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.taxExempt}
              onChange={(e) => updateField('taxExempt', e.target.checked)}
              className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            <span className="text-primary-300">Tax Exempt</span>
          </label>

          {formData.taxExempt && (
            <div>
              <label className="block text-sm font-medium text-primary-300 mb-2">Tax Exempt Number</label>
              <input
                type="text"
                value={formData.taxExemptNumber || ''}
                onChange={(e) => updateField('taxExemptNumber', e.target.value || undefined)}
                className="w-full max-w-xs px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Compliance Tab
function ComplianceTab({ formData, updateField, errors }: TabProps) {
  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Engagement</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">Engagement Date</label>
            <input
              type="date"
              value={formData.engagementDate || ''}
              onChange={(e) => updateField('engagementDate', e.target.value || undefined)}
              className="w-full max-w-xs px-3 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:border-accent-500 outline-none"
            />
          </div>

          <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.engagementLetterSigned}
              onChange={(e) => updateField('engagementLetterSigned', e.target.checked)}
              className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            <div className="flex-1">
              <span className="text-white font-medium">Engagement Letter Signed</span>
              <p className="text-sm text-primary-400">Client has signed the engagement letter</p>
            </div>
            {formData.engagementLetterSigned ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-400" />
            )}
          </label>
        </div>
      </div>

      {/* Conflict Check */}
      <div className="border-t border-primary-700 pt-6">
        <h3 className="text-lg font-medium text-white mb-4">Conflict Check</h3>

        <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={formData.conflictCheckCompleted}
            onChange={(e) => updateField('conflictCheckCompleted', e.target.checked)}
            className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
          />
          <div className="flex-1">
            <span className="text-white font-medium">Conflict Check Completed</span>
            <p className="text-sm text-primary-400">Conflicts of interest have been checked</p>
          </div>
          {formData.conflictCheckCompleted ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-400" />
          )}
        </label>
      </div>

      {/* Identity Verification */}
      <div className="border-t border-primary-700 pt-6">
        <h3 className="text-lg font-medium text-white mb-4">Identity Verification</h3>

        <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={formData.identityVerified}
            onChange={(e) => updateField('identityVerified', e.target.checked)}
            className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
          />
          <div className="flex-1">
            <span className="text-white font-medium">Identity Verified</span>
            <p className="text-sm text-primary-400">Client identity has been verified</p>
          </div>
          {formData.identityVerified ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-400" />
          )}
        </label>
      </div>

      {/* Compliance Summary */}
      <div className="border-t border-primary-700 pt-6">
        <h3 className="text-lg font-medium text-white mb-4">Compliance Summary</h3>

        <div className="grid grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg text-center ${
            formData.engagementLetterSigned ? 'bg-green-500/10' : 'bg-yellow-500/10'
          }`}>
            <FileCheck className={`w-6 h-6 mx-auto mb-2 ${
              formData.engagementLetterSigned ? 'text-green-400' : 'text-yellow-400'
            }`} />
            <p className="text-sm text-primary-300">Engagement</p>
            <p className={`text-xs ${
              formData.engagementLetterSigned ? 'text-green-400' : 'text-yellow-400'
            }`}>
              {formData.engagementLetterSigned ? 'Complete' : 'Pending'}
            </p>
          </div>
          <div className={`p-4 rounded-lg text-center ${
            formData.conflictCheckCompleted ? 'bg-green-500/10' : 'bg-yellow-500/10'
          }`}>
            <Shield className={`w-6 h-6 mx-auto mb-2 ${
              formData.conflictCheckCompleted ? 'text-green-400' : 'text-yellow-400'
            }`} />
            <p className="text-sm text-primary-300">Conflicts</p>
            <p className={`text-xs ${
              formData.conflictCheckCompleted ? 'text-green-400' : 'text-yellow-400'
            }`}>
              {formData.conflictCheckCompleted ? 'Cleared' : 'Pending'}
            </p>
          </div>
          <div className={`p-4 rounded-lg text-center ${
            formData.identityVerified ? 'bg-green-500/10' : 'bg-yellow-500/10'
          }`}>
            <User className={`w-6 h-6 mx-auto mb-2 ${
              formData.identityVerified ? 'text-green-400' : 'text-yellow-400'
            }`} />
            <p className="text-sm text-primary-300">Identity</p>
            <p className={`text-xs ${
              formData.identityVerified ? 'text-green-400' : 'text-yellow-400'
            }`}>
              {formData.identityVerified ? 'Verified' : 'Pending'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Preferences Tab
function PreferencesTab({ formData, updateField, errors }: TabProps) {
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Portal Access */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Client Portal</h3>

        <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={formData.portalAccessEnabled}
            onChange={(e) => updateField('portalAccessEnabled', e.target.checked)}
            className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
          />
          <div className="flex-1">
            <span className="text-white font-medium">Enable Portal Access</span>
            <p className="text-sm text-primary-400">Allow client to access the client portal</p>
          </div>
        </label>
      </div>

      {/* Email Preferences */}
      <div className="border-t border-primary-700 pt-6">
        <h3 className="text-lg font-medium text-white mb-4">Email Preferences</h3>

        <div className="space-y-3">
          <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.receiveInvoiceEmails}
              onChange={(e) => updateField('receiveInvoiceEmails', e.target.checked)}
              className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            <div className="flex-1">
              <span className="text-white font-medium">Invoice Emails</span>
              <p className="text-sm text-primary-400">Receive invoices and billing statements</p>
            </div>
            <Mail className="w-5 h-5 text-primary-500" />
          </label>

          <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.receiveMatterUpdates}
              onChange={(e) => updateField('receiveMatterUpdates', e.target.checked)}
              className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            <div className="flex-1">
              <span className="text-white font-medium">Matter Updates</span>
              <p className="text-sm text-primary-400">Receive updates about matter progress</p>
            </div>
            <Bell className="w-5 h-5 text-primary-500" />
          </label>

          <label className="flex items-center gap-3 p-4 bg-primary-800/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.receiveMarketingEmails}
              onChange={(e) => updateField('receiveMarketingEmails', e.target.checked)}
              className="w-5 h-5 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
            />
            <div className="flex-1">
              <span className="text-white font-medium">Marketing & Newsletters</span>
              <p className="text-sm text-primary-400">Receive firm updates and legal news</p>
            </div>
            <Info className="w-5 h-5 text-primary-500" />
          </label>
        </div>
      </div>
    </div>
  );
}

export default ClientForm;
